import { useEffect } from 'react';

import { getAppForPath, getAppForUrl } from '../utilities/mfeForPath';
import useAnalyticEvent from './useAnalyticEvent';

type NavigationDestinationLike = {
  url: string;
  sameDocument: boolean;
  index: number;
};

type NavigateEventLike = Event & {
  navigationType: 'reload' | 'push' | 'replace' | 'traverse';
  destination: NavigationDestinationLike;
};

type NavigationLike = {
  currentEntry: { index: number } | null;
  addEventListener: (type: 'navigate', listener: (event: NavigateEventLike) => void) => void;
  removeEventListener: (type: 'navigate', listener: (event: NavigateEventLike) => void) => void;
};

declare global {
  interface Window {
    navigation?: NavigationLike;
  }
}

const SHELL_APP = 'stc-shell-ui';

const getPathname = (url: string): string | undefined => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return undefined;
  }
};

const useBackButtonExitHandler = () => {
  const { recordEventKeepAlive } = useAnalyticEvent();

  useEffect(() => {
    const { navigation } = window;

    if (!navigation) {
      return undefined;
    }

    const handleNavigate = (event: NavigateEventLike) => {
      const { destination, navigationType } = event;

      if (navigationType !== 'traverse' || destination.sameDocument) {
        return;
      }

      const toPath = getPathname(destination.url);

      if (toPath && getAppForPath(toPath)) {
        return;
      }

      const currentIndex = navigation.currentEntry?.index;
      const destinationIndex = destination.index;

      let navigationDirection: 'back' | 'forward' | undefined;

      if (typeof currentIndex === 'number' && destinationIndex >= 0) {
        navigationDirection = destinationIndex < currentIndex ? 'back' : 'forward';
      }

      /**
       * Dispatches an event when a browser back press leaves the Sell-to-Carvana app entirely
       *
       * eventName: 'SellToCarvana - Shell:BackButtonExit - Clicked'
       */
      recordEventKeepAlive('Clicked', 'Shell', 'BackButtonExit', {
        fromApp: getAppForUrl(window.location.pathname) ?? SHELL_APP,
        fromPath: window.location.pathname,
        toPath,
        navigationDirection
      });
    };

    navigation.addEventListener('navigate', handleNavigate);
    return () => navigation.removeEventListener('navigate', handleNavigate);
  }, [recordEventKeepAlive]);
};

export default useBackButtonExitHandler;
